import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProjectNote } from '@/api/client';
import { api } from '@/api/client';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/useAuth';
import { useDebouncePerKey } from '@/hooks/useDebouncePerKey';
import { generateUUID } from '@/utils/generateUUID';
import { TEMP_NOTE_PREFIX, isTempNote } from '@/utils/projectNotes';
import { invalidateSummariesForProjectIds } from '@/lib/summariesBatchReload';
import { sortProjectNotesNewestFirst } from './projectNotesPopoverUtils';
import { PROJECT_NOTES_AUTO_SAVE_DEBOUNCE_MS } from './projectNotesPopoverConstants';
import { getProjectNotesQueryOptions } from './projectNotesPopoverQuery';

export interface ProjectNotesEditor {
  /** Заметки, свежие сверху */
  notes: ProjectNote[];
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  getNoteContent: (note: ProjectNote) => string;
  changeNote: (noteId: string, content: string) => void;
  addNote: () => void;
  deleteNote: (noteId: string) => void;
  deletingId: string | null;
  isDeleteBusy: boolean;
  isAddBusy: boolean;
}

/**
 * Заметки существующего проекта: загрузка, автосохранение с debounce, оптимистичные create/update/delete.
 * `active` — заметки видны пользователю: включает загрузку и тост об ошибке;
 * при переходе в false черновики сохраняются (flush), при повторном true — сбрасываются.
 */
export function useProjectNotesEditor(projectId: string, active: boolean): ProjectNotesEditor {
  const queryClient = useQueryClient();
  const { showError: showToastError } = useToast();
  const { user } = useAuth();

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [draftContent, setDraftContent] = useState<Map<string, string>>(new Map());
  const draftContentRef = useRef<Map<string, string>>(new Map());
  draftContentRef.current = draftContent;
  const dirtyIdsRef = useRef<Set<string>>(new Set());
  /** tempId заметки, для которой сейчас выполняется POST create */
  const creatingTempIdsRef = useRef<Set<string>>(new Set());
  /** Текст, введённый до завершения create — применяется patch после успеха */
  const pendingContentAfterCreateRef = useRef<Map<string, string>>(new Map());

  const {
    data: notes,
    isPending,
    isError,
    error,
  } = useQuery({
    ...getProjectNotesQueryOptions(projectId),
    enabled: active,
  });

  const removeTempNote = useCallback(
    (tempId: string) => {
      queryClient.setQueryData<ProjectNote[]>(['projectNotes', projectId], (list) =>
        (list ?? []).filter((n) => n.note_id !== tempId)
      );
    },
    [queryClient, projectId]
  );

  const invalidateResourceSummaries = useCallback(() => {
    invalidateSummariesForProjectIds(queryClient, [projectId]);
  }, [queryClient, projectId]);

  const fetchErrorToastedRef = useRef(false);
  useEffect(() => {
    if (!active || !isError) {
      fetchErrorToastedRef.current = false;
      return;
    }
    if (!error || fetchErrorToastedRef.current) return;
    fetchErrorToastedRef.current = true;
    showToastError((error as Error).message || 'Не удалось загрузить заметки');
  }, [active, isError, error, showToastError]);

  const updateMutation = useMutation({
    mutationFn: ({ noteId, note_content }: { noteId: string; note_content: string }) =>
      api.projectNotes.update(projectId, noteId, { note_content }),
    onMutate: async ({ noteId, note_content }) => {
      await queryClient.cancelQueries({ queryKey: ['projectNotes', projectId] });
      const previousNotes = queryClient.getQueryData<ProjectNote[]>(['projectNotes', projectId]);
      queryClient.setQueryData<ProjectNote[]>(['projectNotes', projectId], (old) =>
        (old ?? []).map((n) => (n.note_id === noteId ? { ...n, note_content } : n))
      );
      return { previousNotes };
    },
    onError: (err, _vars, context) => {
      if (context?.previousNotes !== undefined) {
        queryClient.setQueryData(['projectNotes', projectId], context.previousNotes);
      }
      showToastError((err as Error).message || 'Ошибка');
    },
    onSuccess: (data) => {
      queryClient.setQueryData<ProjectNote[]>(['projectNotes', projectId], (old) =>
        (old ?? []).map((n) => (n.note_id === data.note_id ? data : n))
      );
      setDraftContent((prev) => {
        const current = prev.get(data.note_id);
        if (current !== undefined && current === data.note_content) {
          const next = new Map(prev);
          next.delete(data.note_id);
          return next;
        }
        return prev;
      });
    },
    onSettled: invalidateResourceSummaries,
  });

  const deleteMutation = useMutation({
    mutationFn: (noteId: string) => api.projectNotes.delete(projectId, noteId),
    onMutate: async (noteId: string) => {
      await queryClient.cancelQueries({ queryKey: ['projectNotes', projectId] });
      dirtyIdsRef.current.delete(noteId);
      const previousNotes = queryClient.getQueryData<ProjectNote[]>(['projectNotes', projectId]);
      queryClient.setQueryData<ProjectNote[]>(['projectNotes', projectId], (old) =>
        (old ?? []).filter((n) => n.note_id !== noteId)
      );
      return { previousNotes };
    },
    onError: (err, _noteId, context) => {
      if (context?.previousNotes !== undefined) {
        queryClient.setQueryData(['projectNotes', projectId], context.previousNotes);
      }
      showToastError((err as Error).message || 'Ошибка');
    },
    onSettled: () => {
      setDeletingId(null);
      invalidateResourceSummaries();
    },
  });

  const createMutation = useMutation({
    mutationFn: ({
      note_content,
      created_by,
    }: {
      note_content: string;
      tempId: string;
      created_by?: string | null;
    }) => api.projectNotes.create(projectId, { note_content, created_by }),
    onMutate: async ({ tempId, note_content, created_by }) => {
      await queryClient.cancelQueries({ queryKey: ['projectNotes', projectId] });
      const previousNotes = queryClient.getQueryData<ProjectNote[]>(['projectNotes', projectId]);
      const currentNotes = previousNotes ?? [];
      creatingTempIdsRef.current.add(tempId);
      const optimisticNote: ProjectNote = {
        note_id: tempId,
        project_id: projectId,
        note_content,
        created_at: new Date().toISOString(),
        created_by: created_by ?? null,
        display_order: currentNotes.length,
      };
      queryClient.setQueryData<ProjectNote[]>(['projectNotes', projectId], [optimisticNote, ...currentNotes]);
      return { previousNotes, tempId };
    },
    onError: (err, _vars, context) => {
      if (context?.tempId != null) {
        pendingContentAfterCreateRef.current.delete(context.tempId);
      }
      if (context?.previousNotes !== undefined) {
        queryClient.setQueryData(['projectNotes', projectId], context.previousNotes);
      }
      showToastError((err as Error).message || 'Ошибка');
    },
    onSuccess: (data, _vars, context) => {
      const tempId = context?.tempId;
      if (!tempId) return;
      queryClient.setQueryData<ProjectNote[]>(['projectNotes', projectId], (old) =>
        (old ?? []).map((n) => (n.note_id === tempId ? data : n))
      );
      setDraftContent((prev) => {
        const next = new Map(prev);
        next.delete(tempId);
        const pending = pendingContentAfterCreateRef.current.get(tempId);
        if (pending !== undefined && pending !== '') {
          next.set(data.note_id, pending);
        }
        return next;
      });
      const pendingRaw = pendingContentAfterCreateRef.current.get(tempId);
      pendingContentAfterCreateRef.current.delete(tempId);
      if (pendingRaw !== undefined && pendingRaw !== data.note_content) {
        updateMutation.mutate({ noteId: data.note_id, note_content: pendingRaw.trim() });
      }
    },
    onSettled: (_data, _err, variables) => {
      creatingTempIdsRef.current.delete(variables.tempId);
      invalidateResourceSummaries();
    },
  });

  const performSaveNote = useCallback(
    (noteId: string) => {
      const raw = draftContentRef.current.get(noteId) ?? '';
      const content = raw.trim();
      const currentNotes = queryClient.getQueryData<ProjectNote[]>(['projectNotes', projectId]) ?? [];
      const note = currentNotes.find((n) => n.note_id === noteId);
      if (!note) return;

      if (isTempNote(noteId)) {
        if (creatingTempIdsRef.current.has(noteId)) {
          pendingContentAfterCreateRef.current.set(noteId, raw);
          return;
        }
        if (content) {
          createMutation.mutate({
            note_content: content,
            tempId: noteId,
            created_by: user?.id ?? null,
          });
        } else {
          removeTempNote(noteId);
          setDraftContent((prev) => {
            const next = new Map(prev);
            next.delete(noteId);
            return next;
          });
        }
      } else {
        if (raw === note.note_content) return;
        dirtyIdsRef.current.delete(noteId);
        updateMutation.mutate({ noteId, note_content: content });
      }
    },
    [projectId, queryClient, createMutation, updateMutation, removeTempNote, user?.id]
  );

  const [scheduleSave, flush] = useDebouncePerKey(PROJECT_NOTES_AUTO_SAVE_DEBOUNCE_MS, performSaveNote);

  useEffect(() => {
    if (active) {
      dirtyIdsRef.current.clear();
      setDraftContent(new Map());
    } else {
      flush();
    }
  }, [active, flush]);

  const addNote = useCallback(() => {
    const tempId = `${TEMP_NOTE_PREFIX}${generateUUID()}`;
    createMutation.mutate({
      note_content: '',
      tempId,
      created_by: user?.id ?? null,
    });
  }, [createMutation, user?.id]);

  const changeNote = useCallback(
    (noteId: string, content: string) => {
      if (!isTempNote(noteId)) dirtyIdsRef.current.add(noteId);
      setDraftContent((prev) => {
        const next = new Map(prev);
        next.set(noteId, content);
        return next;
      });
      scheduleSave(noteId);
    },
    [scheduleSave]
  );

  const getNoteContent = useCallback(
    (note: ProjectNote) => draftContent.get(note.note_id) ?? note.note_content,
    [draftContent]
  );

  const deleteNote = useCallback(
    (noteId: string) => {
      if (deletingId) return;
      if (!window.confirm('Удалить заметку?')) return;
      setDeletingId(noteId);
      deleteMutation.mutate(noteId);
    },
    [deletingId, deleteMutation]
  );

  const notesSorted = useMemo(() => sortProjectNotesNewestFirst(notes ?? []), [notes]);

  return {
    notes: notesSorted,
    isPending,
    isError,
    error: error as Error | null,
    getNoteContent,
    changeNote,
    addNote,
    deleteNote,
    deletingId,
    isDeleteBusy: deleteMutation.isPending,
    isAddBusy: isPending || createMutation.isPending || updateMutation.isPending,
  };
}
